import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";

// Assets
import shreeLogo from "../../assets/portfolio/Group.svg";
import shreePreview1 from "../../assets/portfolio/shree-preview.png";
import shreePreview2 from "../../assets/portfolio/shree-preview1.png";
import shreePreview3 from "../../assets/portfolio/shree-preview2.png";
import shreePreview4 from "../../assets/portfolio/shree-preview3.png";
import shreePreview5 from "../../assets/portfolio/shree-preview4.png";

// Tech icons
import { FaReact, FaAws } from "react-icons/fa";
import { SiTailwindcss, SiFirebase, SiMongodb } from "react-icons/si";

/* ================= AUTO SWIPE PREVIEW ================= */
function AutoSwipePreviews({ previews }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % previews.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [previews.length]);

  return (
    <div className="relative w-full overflow-hidden">
      <div className="flex justify-center">
        <AnimatePresence mode="wait">
          <motion.img
            key={index}
            src={previews[index]}
            alt="Shree Electronics Website Preview"
            initial={{ x: 120, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -120, opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="
              w-[720px] md:w-[900px]
              rounded-3xl
              border border-black/10
              shadow-2xl
            "
          />
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ================= MAIN PAGE ================= */
export default function ShreePage() {
  const previews = [
    shreePreview1,
    shreePreview2,
    shreePreview3,
    shreePreview4,
    shreePreview5,
  ];

  return (
    <div className="w-full bg-transparent text-black pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-20">

        {/* ================= HERO ================= */}
        <section className="flex flex-col items-center gap-10 text-center">

          {/* TEXT */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="hero-heading mb-4">
              Shree Electronics
            </h1>

            <p className="hero-copy">
              A modern ecommerce platform built for an online electronics retail business,
              designed for clarity, performance, and real-world usability.
            </p>
          </motion.div>

          {/* LOGO */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            <a
              href="https://www.shreeelectronicsbazar.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group"
            >
              <img
                src={shreeLogo}
                alt="Shree Electronics Bazar"
                className="
                  w-64 h-64 object-contain
                  drop-shadow-[0_25px_35px_rgba(0,0,0,0.15)]
                  transition-transform duration-300
                  group-hover:scale-105
                "
              />
            </a>
          </motion.div>

        </section>

        {/* ================= OVERVIEW ================= */}
        <section className="mt-32">
          <div className="mx-auto max-w-5xl text-center">
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="section-heading mb-6"
            >
              Project Overview
            </motion.h2>

            <p className="section-copy">
              Shree Electronics Bazar is a retail-focused electronics business that
              needed a reliable and modern digital presence to sell products online.
              The objective was to move beyond a basic website and create a scalable
              e-commerce platform that works smoothly across devices, is easy to
              manage for a non-technical team, and performs well under real customer
              traffic.
            </p>

            <p className="section-copy mt-6">
              The product was designed to balance simplicity and functionality,
              allowing customers to browse products easily while ensuring the
              business can manage listings, categories, and content without
              operational complexity.
            </p>
          </div>
        </section>

        {/* ================= WEBSITE PREVIEWS ================= */}
        <section className="mt-24">
          <AutoSwipePreviews previews={previews} />

          <div className="mt-12 flex justify-center">
            <a
              href="https://www.shreeelectronicsbazar.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 font-medium tracking-wide text-black group"
            >
              <span>Visit Website</span>
              <span className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                >
                  <path d="M7 17l10-10" />
                  <path d="M7 7h10v10" />
                </svg>
              </span>
            </a>
          </div>
        </section>

        {/* ================= OUR STRATEGY ================= */}
        <section className="mx-auto mt-32 max-w-5xl text-center">
          <h2 className="section-heading mb-6">
            Our Strategy
          </h2>

          <p className="section-copy">
            Our approach focused on building a platform that feels familiar to
            everyday users while remaining robust under the hood. We prioritized
            fast load times, mobile-first layouts, and a clean browsing experience
            to reduce friction for customers.
          </p>

          <p className="section-copy mt-6">
            From a business perspective, the system was designed to scale —
            enabling future expansion, product growth, and feature upgrades
            without needing a complete rebuild.
          </p>
        </section>

        {/* ================= WHAT WE BUILT ================= */}
        <section className="mt-32">
          <h2 className="section-heading mb-10">
            What We Built
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              "Custom e-commerce storefront",
              "Structured product & category system",
              "Mobile-first responsive layouts",
              "SEO-optimized page structure",
              "Admin-friendly content management",
              "Performance-focused frontend",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="
                  bg-white border border-black/10 rounded-xl
                  p-6 shadow-sm hover:shadow-xl transition
                "
              >
                <h3 className="text-[15px] font-medium">{item}</h3>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ================= TECH STACK ================= */}
        <section className="mt-32">
          <h2 className="section-heading mb-10">
            Tech Stack
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {[
              { name: "React", icon: <FaReact /> },
              { name: "Tailwind CSS", icon: <SiTailwindcss /> },
              { name: "Firebase Hosting", icon: <SiFirebase /> },
              { name: "AWS Server", icon: <FaAws /> },
              { name: "MongoDB", icon: <SiMongodb /> },
            ].map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="
                  flex flex-col items-center justify-center
                  border border-black/10 rounded-xl
                  p-6 shadow-sm hover:shadow-xl transition
                  group
                "
              >
                <div className="text-3xl mb-3 text-black/80 group-hover:scale-110 transition">
                  {t.icon}
                </div>
                <span className="text-[14px] font-medium">{t.name}</span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ================= CTA ================= */}
        <section className="mt-32 text-center">
          <h2 className="section-heading mb-6">
            Want a Product Like This?
          </h2>

          <p className="mx-auto mb-8 max-w-xl text-center text-neutral-600">
            We help businesses turn ideas and requirements into scalable,
            production-ready digital products.
          </p>

          <a
            href="/contact"
            className="
              inline-block px-10 py-3 rounded-full
              bg-black text-white font-medium
              hover:bg-opacity-80 transition
            "
          >
            Start Your Project
          </a>
        </section>

      </div>
    </div>
  );
}
